import {
  SafetyModalsState,
  ModalType,
  ModalPriority,
  CrisisSeverity,
  hasHigherPriority,
} from './types-constants';

export type SafetyModalsAction =
  | { type: 'INITIALIZE'; payload: { tosAccepted: boolean; tosAcceptedAt?: string; tosVersion?: string } }
  | { type: 'OPEN_MODAL'; payload: { modalType: ModalType; data?: Record<string, unknown> } }
  | { type: 'CLOSE_MODAL'; payload: { modalType: ModalType } }
  | { type: 'ACCEPT_TOS'; payload: { timestamp: string; version: string } }
  | { type: 'DECLINE_TOS' }
  | { type: 'TRIGGER_CRISIS'; payload: { severity?: CrisisSeverity } }
  | { type: 'DISMISS_CRISIS' }
  | { type: 'PROCESS_QUEUE' };

export const initialState: SafetyModalsState = {
  modals: {
    [ModalType.TOS]: {
      type: ModalType.TOS,
      priority: ModalPriority.CRITICAL,
      isVisible: false,
      isPending: false,
    },
    [ModalType.CRISIS_RESOURCES]: {
      type: ModalType.CRISIS_RESOURCES,
      priority: ModalPriority.HIGH,
      isVisible: false,
      isPending: false,
    },
    [ModalType.CRISIS_PRACTITIONER]: {
      type: ModalType.CRISIS_PRACTITIONER,
      priority: ModalPriority.MEDIUM_HIGH,
      isVisible: false,
      isPending: false,
    },
    [ModalType.CONCERN_FLAG]: {
      type: ModalType.CONCERN_FLAG,
      priority: ModalPriority.MEDIUM,
      isVisible: false,
      isPending: false,
    },
    [ModalType.DETECTION_FEEDBACK]: {
      type: ModalType.DETECTION_FEEDBACK,
      priority: ModalPriority.LOW,
      isVisible: false,
      isPending: false,
    },
  },
  priorityQueue: [],
  tosAccepted: false,
  activeModal: null,
  crisisDetected: false,
  isInitialized: false,
};

export function safetyModalsReducer(
  state: SafetyModalsState,
  action: SafetyModalsAction
): SafetyModalsState {
  switch (action.type) {
    case 'INITIALIZE': {
      return {
        ...state,
        tosAccepted: action.payload.tosAccepted,
        tosAcceptedAt: action.payload.tosAcceptedAt,
        tosVersion: action.payload.tosVersion,
        isInitialized: true,
      };
    }
    case 'OPEN_MODAL': {
      const { modalType, data } = action.payload;
      if (!state.tosAccepted && modalType !== ModalType.TOS) {
        return {
          ...state,
          modals: {
            ...state.modals,
            [ModalType.TOS]: {
              ...state.modals[ModalType.TOS],
              isVisible: true,
              isPending: false,
            },
            [modalType]: {
              ...state.modals[modalType],
              isPending: true,
              data,
            },
          },
          activeModal: ModalType.TOS,
          priorityQueue: state.priorityQueue.includes(modalType)
            ? state.priorityQueue
            : [...state.priorityQueue, modalType],
        };
      }
      if (state.activeModal && !hasHigherPriority(modalType, state.activeModal)) {
        return {
          ...state,
          modals: {
            ...state.modals,
            [modalType]: {
              ...state.modals[modalType],
              isPending: true,
              data,
            },
          },
          priorityQueue: state.priorityQueue.includes(modalType)
            ? state.priorityQueue
            : [...state.priorityQueue, modalType],
        };
      }
      let newState = { ...state };
      if (state.activeModal) {
        newState = {
          ...newState,
          modals: {
            ...newState.modals,
            [state.activeModal]: {
              ...newState.modals[state.activeModal],
              isVisible: false,
              isPending: true,
            },
          },
          priorityQueue: newState.priorityQueue.includes(state.activeModal)
            ? newState.priorityQueue
            : [...newState.priorityQueue, state.activeModal],
        };
      }
      return {
        ...newState,
        modals: {
          ...newState.modals,
          [modalType]: {
            ...newState.modals[modalType],
            isVisible: true,
            isPending: false,
            data,
          },
        },
        activeModal: modalType,
        priorityQueue: newState.priorityQueue.filter((m) => m !== modalType),
      };
    }
    case 'CLOSE_MODAL': {
      const { modalType } = action.payload;
      const newState = {
        ...state,
        modals: {
          ...state.modals,
          [modalType]: {
            ...state.modals[modalType],
            isVisible: false,
            isPending: false,
            data: undefined,
          },
        },
      };
      if (state.activeModal === modalType) {
        return {
          ...newState,
          activeModal: null,
        };
      }
      return newState;
    }
    case 'ACCEPT_TOS': {
      return {
        ...state,
        tosAccepted: true,
        tosAcceptedAt: action.payload.timestamp,
        tosVersion: action.payload.version,
        modals: {
          ...state.modals,
          [ModalType.TOS]: {
            ...state.modals[ModalType.TOS],
            isVisible: false,
            isPending: false,
          },
        },
        activeModal: state.activeModal === ModalType.TOS ? null : state.activeModal,
      };
    }
    case 'DECLINE_TOS': {
      return {
        ...state,
        modals: {
          ...state.modals,
          [ModalType.TOS]: {
            ...state.modals[ModalType.TOS],
            isVisible: false,
            isPending: false,
          },
        },
        activeModal: state.activeModal === ModalType.TOS ? null : state.activeModal,
        priorityQueue: [],
      };
    }
    case 'TRIGGER_CRISIS': {
      return {
        ...state,
        crisisDetected: true,
        crisisSeverity: action.payload.severity,
      };
    }
    case 'DISMISS_CRISIS': {
      return {
        ...state,
        crisisDetected: false,
        crisisSeverity: undefined,
        modals: {
          ...state.modals,
          [ModalType.CRISIS_RESOURCES]: {
            ...state.modals[ModalType.CRISIS_RESOURCES],
            isVisible: false,
            isPending: false,
          },
        },
        activeModal:
          state.activeModal === ModalType.CRISIS_RESOURCES ? null : state.activeModal,
      };
    }
    case 'PROCESS_QUEUE': {
      if (state.priorityQueue.length === 0 || state.activeModal) {
        return state;
      }
      const nextModal = state.priorityQueue.reduce((highest, current) =>
        hasHigherPriority(current, highest) ? current : highest
      );
      return {
        ...state,
        modals: {
          ...state.modals,
          [nextModal]: {
            ...state.modals[nextModal],
            isVisible: true,
            isPending: false,
          },
        },
        activeModal: nextModal,
        priorityQueue: state.priorityQueue.filter((m) => m !== nextModal),
      };
    }
    default:
      return state;
  }
}
